// src/components/CourierDetailModal.jsx
import React, { useState, useEffect } from 'react';
import { getCourierAvatar } from '../utils/courierAvatar';
import { formatAddress, formatContactInfo } from '../services/courierSelectionService';
import './CourierDetailModal.css';

/**
 * Modal to display courier and branch details
 * Shows courier logo/initials + name, and branch contact/address information
 * 
 * @param {Object} courier - Courier option object from getCouriersByDistrict()
 * @param {boolean} isOpen - Whether modal is visible
 * @param {Function} onClose - Callback when modal is closed
 * @param {Function} onSelect - Callback when user selects this courier
 * @param {boolean} isLoading - Whether data is loading
 */
const CourierDetailModal = ({ courier, isOpen, onClose, onSelect, isLoading = false }) => {
  const [avatarData, setAvatarData] = useState(null);

  useEffect(() => {
    if (courier) {
      const avatar = getCourierAvatar(courier);
      setAvatarData(avatar);
    }
  }, [courier]);

  if (!isOpen || !courier) {
    return null;
  }

  const avatar = avatarData || getCourierAvatar(courier);
  const contactInfo = formatContactInfo({
    contactNumber: courier.contactNumber,
    alternateContactNumber: courier.alternateContactNumber,
    email: courier.email
  });
  const formattedAddress = formatAddress(courier.address);

  return (
    <>
      {/* Backdrop */}
      <div className="courier-modal-backdrop" onClick={onClose} />

      {/* Modal */}
      <div className="courier-modal">
        {/* Close button */}
        <button className="courier-modal-close" onClick={onClose}>
          ✕
        </button>

        {/* Header with Logo/Avatar and Courier Name */}
        <div className="courier-modal-header">
          <div className="courier-avatar-container">
            {avatar.type === 'logo' && avatar.url ? (
              <img
                src={avatar.url}
                alt={avatar.alt}
                className="courier-logo"
                onError={(e) => {
                  // Fallback to initials if logo fails to load
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
            ) : null}
            
            <div
              className="courier-avatar-initials"
              style={{
                backgroundColor: avatar.backgroundColor,
                display: avatar.type === 'logo' && avatar.url ? 'none' : 'flex'
              }}
            >
              {avatar.initials}
            </div>
          </div>

          <div className="courier-header-info">
            <h2 className="courier-name">{courier.courierName}</h2>
            {courier.courierWebsite && (
              <a
                href={courier.courierWebsite}
                target="_blank"
                rel="noopener noreferrer"
                className="courier-website"
              >
                {courier.courierWebsite}
              </a>
            )}
          </div>
        </div>

        {/* Branch Details Section */}
        <div className="courier-modal-content">
          {/* Branch Name & Code */}
          <div className="section">
            <h3 className="section-title">📍 Branch Details</h3>
            <div className="detail-row">
              <span className="detail-label">Branch Name:</span>
              <span className="detail-value">{courier.branchName}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Branch Code:</span>
              <span className="detail-value code">{courier.branchCode}</span>
            </div>
          </div>

          {/* Contact Person */}
          <div className="section">
            <h3 className="section-title">👤 Contact Person</h3>
            <div className="detail-row">
              <span className="detail-label">Name:</span>
              <span className="detail-value">{courier.contactPerson || 'N/A'}</span>
            </div>
          </div>

          {/* Contact Information */}
          {contactInfo.length > 0 && (
            <div className="section">
              <h3 className="section-title">📞 Contact Information</h3>
              {contactInfo.map((info, index) => (
                <div key={index} className="detail-row">
                  <span className="detail-value contact-info">{info}</span>
                </div>
              ))}
            </div>
          )}

          {/* Address */}
          {formattedAddress && (
            <div className="section">
              <h3 className="section-title">🏢 Address</h3>
              <div className="detail-row address-row">
                <span className="detail-value address-text">{formattedAddress}</span>
              </div>
            </div>
          )}

          {/* Address Details if available */}
          {courier.address && (
            <div className="section">
              <h3 className="section-title">📬 Location</h3>
              {courier.address.districtName && (
                <div className="detail-row">
                  <span className="detail-label">District:</span>
                  <span className="detail-value">{courier.address.districtName}</span>
                </div>
              )}
              {courier.address.stateName && (
                <div className="detail-row">
                  <span className="detail-label">State:</span>
                  <span className="detail-value">{courier.address.stateName}</span>
                </div>
              )}
              {courier.address.pinCode && (
                <div className="detail-row">
                  <span className="detail-label">Pin Code:</span>
                  <span className="detail-value">{courier.address.pinCode}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="courier-modal-footer">
          <button
            className="btn btn-close"
            onClick={onClose}
            disabled={isLoading}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
};

export default CourierDetailModal;
